import React, { useState } from 'react';
import { Box, TextField, Typography, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { generateSummary, improveComment } from '../../../../../api/openai/openaiAPI';
import {
	CommentFormState,
	CommentFormStateErrors,
} from '../../hooks/useCommentEditorForm';

interface ActivityTextProps {
	formState: CommentFormState;
	handleChange: <T extends keyof CommentFormState>(
		field: T,
		value: CommentFormState[T],
		validate?: boolean,
	) => void;
	errors: CommentFormStateErrors;
	resizableComment?: boolean;
}

const ActivityText: React.FC<ActivityTextProps> = (props) => {
	const { formState, handleChange, errors, resizableComment } = props;
	const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
	const [isImprovingComment, setIsImprovingComment] = useState(false);

	const handleAutoFillSummary = async () => {
		// Check if there's sufficient content in the description field to summarize
		if (!formState.description || formState.description.trim().length < 10) {
			// Fallback to the original behavior if insufficient description
			handleChange('title', 'this is a summary');
			return;
		}

		setIsGeneratingSummary(true);
		
		try {
			const summary = await generateSummary(formState.description);
			handleChange('title', summary);
		} catch (error) {
			console.error('Error generating AI summary:', error);
			// Fallback to default text on error
			handleChange('title', 'Error generating summary - please try again');
		} finally {
			setIsGeneratingSummary(false);
		}
	};

	const handleImproveComment = async () => {
		// Check if there's sufficient content in the description field to improve
		if (!formState.description || formState.description.trim().length < 10) {
			// Fallback behavior if insufficient description
			return;
		}

		setIsImprovingComment(true);
		
		try {
			const improvedComment = await improveComment(formState.description);
			handleChange('description', improvedComment);
		} catch (error) {
			console.error('Error improving comment:', error);
			// Could show a toast notification here instead of changing the text
			console.warn('Failed to improve comment - please try again');
		} finally {
			setIsImprovingComment(false);
		}
	};

	// Helper function to check if AI summary should be enabled
	const isAISummaryEnabled = () => {
		return formState.description && formState.description.trim().length >= 10;
	};

	// Helper function to check if AI comment improvement should be enabled
	const isAICommentImprovementEnabled = () => {
		return formState.description && formState.description.trim().length >= 10;
	};

	return (
		<Box display={'flex'} flexDirection={'column'} gap={1} width={'100%'}>
			<TextField
				value={formState.title}
				onChange={(e) => handleChange('title', e.target.value)}
				label="Summary"
				variant="outlined"
				fullWidth
				multiline
				error={!!errors.title}
				helperText={errors.title}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								onClick={handleAutoFillSummary}
								edge="end"
								aria-label="generate AI summary"
								disabled={isGeneratingSummary || !isAISummaryEnabled()}
								title={
									!formState.description || formState.description.trim().length === 0
										? "Add content to the comment field to generate an AI summary"
										: formState.description.trim().length < 10
										? `Add at least ${10 - formState.description.trim().length} more characters to generate AI summary`
										: "Generate AI summary from comment"
								}
								sx={{
									opacity: isAISummaryEnabled() ? 1 : 0.3,
									transition: 'opacity 0.2s ease-in-out'
								}}
							>
								{isGeneratingSummary ? (
									<CircularProgress size={20} />
								) : (
									<AutoAwesomeIcon />
								)}
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
			<TextField
				value={formState.description}
				onChange={(e) => handleChange('description', e.target.value)}
				label="Comment"
				variant="outlined"
				fullWidth
				multiline
				minRows={resizableComment ? 4 : 10}
				maxRows={resizableComment ? undefined : 20}
				error={!!errors.description}
				helperText={errors.description}
				sx={resizableComment ? {
					'& .MuiInputBase-inputMultiline': {
						resize: 'vertical',
					},
				} : undefined}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}>
							<IconButton
								onClick={handleImproveComment}
								edge="end"
								aria-label="improve comment with AI"
								disabled={isImprovingComment || !isAICommentImprovementEnabled()}
								title={
									!formState.description || formState.description.trim().length === 0
										? "Add content to improve with AI"
										: formState.description.trim().length < 10
										? `Add at least ${10 - formState.description.trim().length} more characters to improve with AI`
										: "Improve comment with AI - better grammar, formatting, and structure"
								}
								sx={{
									opacity: isAICommentImprovementEnabled() ? 1 : 0.3,
									transition: 'opacity 0.2s ease-in-out'
								}}
							>
								{isImprovingComment ? (
									<CircularProgress size={20} />
								) : (
									<AutoAwesomeIcon />
								)}
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
		</Box>
	);
};

export default ActivityText;
